/**
 * URL Index Overview API Service
 * 
 * Unified endpoint for URL indexing status from GSC and Bing.
 * Replaces the old url-coverage and index/status endpoints.
 */

import customerApi, { USE_MOCK_DATA } from './customerApi';
import { UrlIndexOverviewResponse } from '@/types/customer';

// Mock data for development
const mockData: UrlIndexOverviewResponse = {
  data: {
    siteId: 1,
    summary: {
      totalUrls: 21,
      indexed: 5,
      notIndexed: 16,
      indexCoverage: 23.8,
      lastUpdated: new Date().toISOString(),
    },
    engines: {
      GSC: {
        totalUrls: 21,
        indexed: 5,
        notIndexed: 16,
        excluded: 3,
        pending: 16,
        errors: 0,
        statusDistribution: { INDEXED: 5, EXCLUDED: 3, PENDING: 16, ERROR: 0 },
        issues: [
          { reason: 'URL is unknown to Google', count: 12, severity: 'LOW' },
          { reason: 'Submitted and indexed', count: 5, severity: 'LOW' },
          { reason: 'Redirect error', count: 3, severity: 'HIGH' },
        ],
        urls: [
          { url: '/page-1', verdict: 'PASS', coverageState: 'Submitted and indexed', lastCrawlAt: '2026-01-20T10:00:00Z', indexingState: 'INDEXING_ALLOWED' },
          { url: '/page-2', verdict: 'NEUTRAL', coverageState: 'URL is unknown to Google', indexingState: 'INDEXING_STATE_UNSPECIFIED' },
        ],
        lastUpdated: new Date().toISOString(),
      },
      BING: {
        totalUrls: 0,
        indexed: 0,
        notIndexed: 0,
        excluded: 0,
        pending: 0,
        errors: 0,
        statusDistribution: {},
        issues: [],
        urls: [],
        lastUpdated: new Date().toISOString(),
      },
    },
  },
};

export interface GetUrlIndexOverviewParams {
  engine?: 'GSC' | 'BING';
}

/**
 * Get URL index overview data for a site
 */
export async function getUrlIndexOverview(
  siteId: number,
  params?: GetUrlIndexOverviewParams
): Promise<UrlIndexOverviewResponse> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return mockData;
  }

  const queryParams = new URLSearchParams();
  if (params?.engine) queryParams.set('engine', params.engine);

  const queryString = queryParams.toString();
  const url = `/webtools/sites/${siteId}/url-index-overview${queryString ? '?' + queryString : ''}`;

  const response = await customerApi.get<UrlIndexOverviewResponse>(url);
  return response.data;
}

// Legacy alias for backward compatibility
export const getUrlCoverage = getUrlIndexOverview;
export type GetUrlCoverageParams = GetUrlIndexOverviewParams;

export default {
  getUrlIndexOverview,
  getUrlCoverage,
};
